import * as React from 'react';
import { withStyles, StyleRulesCallback, WithStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { IObservableValue, observable, observe } from 'mobx';
import { defaultPageable, emptyPage, Page, Pageable } from '../../api/pageable';
import PaginationNavigation from '../../components/PaginationNavigation';
import { createCatalogue, getModules } from './questionCatalogueApi';
import { Spinner } from '../../spinner/Spinner';
import { QuestionModuleDto } from '../questionModuleTypes';
import QuestionCataloguePreview from '../../components/preview/QuestionModulePreview';
import { translation } from '../../i18n';
import { TextField } from '@material-ui/core';
import { List } from '@material-ui/core';
import { ListItem } from '@material-ui/core';
import { Cancel as CancelIcon } from '@material-ui/icons';
import { Save as SaveIcon } from '@material-ui/icons';
import { ListItemText } from '@material-ui/core';
import { ListItemSecondaryAction } from '@material-ui/core';
import { Button } from '@material-ui/core';
import { log } from '../../logConfig';
import { Typography } from '@material-ui/core';
import { emptyQuestionCatalogueRequest, QuestionCatalogueRequest } from './qustionCatalogueTypes';
import { emptyValidationErrors, isValidationErrors, ValidationErrors } from '../../api/restApi';
import { isValidQuestionCatalogue } from '../../util/validations';
import { createPicker } from '../../util/picker';
import { renderRemainingErrors, textFieldErrorProps } from '../../util/formUtils';
import EmptyPage from '../../components/emptyPage/EmptyPage';
import SaveSnackBar from '../../components/SaveSnackBar';

interface Props {
  renderSearch: (showSearch: boolean) => void;
  searchText: IObservableValue<string>;
}

type LocalStyles = 'previewArea';

const localStyles: StyleRulesCallback<LocalStyles> = theme => ({

  previewArea: {
    padding: theme.spacing.unit * 2,
  },
});

type PropsWithStyle = WithStyles<LocalStyles> & Props;

@observer
class CatalogueTab extends React.Component<PropsWithStyle, {}> {

  @observable private loading: boolean = true;
  @observable private modules: Page<QuestionModuleDto> = emptyPage();
  @observable private pageAble: Pageable = defaultPageable();
  @observable private selectedModules: QuestionModuleDto[] = [];
  @observable private newCatalogue: QuestionCatalogueRequest = emptyQuestionCatalogueRequest();
  @observable private validationErrors: ValidationErrors = emptyValidationErrors();
  private expanded: IObservableValue<string> = observable.box('');
  private saveLoading: IObservableValue<boolean> = observable.box(false);
  private displaySnackbar: IObservableValue<boolean> = observable.box(false);

  constructor(props: PropsWithStyle) {
    super(props);
    this.props.renderSearch(true);
    this.onSaveClicked = this.onSaveClicked.bind(this);
  }

  public componentDidMount(): void {
    observe(
      this.props.searchText,
      () => {
        this.loadModules();
        log.info('Catalogue ' + this.props.searchText.get())
      }
    );
    this.loadModules();
  }

  public render() {
    const errorPicker = createPicker(this.validationErrors.fieldErrors);
    return (
      <>
        {
          this.saveLoading.get() ?
            <Spinner/> :
            <>
              <div style={{width: '100%', textAlign: 'right'}}>
                <div>
                  <Button onClick={this.onSaveClicked}><SaveIcon/> {translation.t('actions.save')}</Button>
                </div>
              </div>
              <div>
                <TextField
                  label={translation.t('catalogue.title')}
                  fullWidth={true}
                  required={true}
                  value={this.newCatalogue.title}
                  onChange={ev => {
                    this.newCatalogue.title = ev.target.value
                  }}
                  {...textFieldErrorProps(errorPicker.pick(e => e.field === 'title'))}
                />
                <Typography variant={'subtitle1'}>{translation.t('catalogue.modules')}:</Typography>
                {this.selectedModules.length === 0 ?
                  <>
                    <Typography>{translation.t('createCatalogueComponent.noModulesSelected')}</Typography>
                    {renderRemainingErrors(errorPicker.pick(e => e.field === 'module'))}
                  </>
                  :
                  <List>
                    {this.selectedModules.map((module, index) => (
                      <ListItem
                        key={module.id + '' + index}
                        button={false}
                      >
                        <ListItemText primary={this.getModuleTitle(module)}/>
                        <ListItemSecondaryAction>
                          <Button onClick={() => this.selectedModules.splice(this.selectedModules.indexOf(module), 1)}>
                            <CancelIcon/>
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                }
                {renderRemainingErrors(errorPicker.remaining())}
              </div>
              <div className={this.props.classes.previewArea}>
                {this.loading ?
                  <Spinner/> :
                  <>
                    {this.modules.content.length === 0 ?
                      <EmptyPage message={translation.t('noEntry.createCatalogue.title')}
                                 searchText={this.props.searchText.get()}
                                 displayFab={false}/> :
                      <>
                        {this.modules.content.map(module =>
                          <QuestionCataloguePreview key={module.id} module={module} expanded={this.expanded}
                                                    handleAdd={this.handleAddModule}/>
                        )}
                        <PaginationNavigation
                          totalPages={this.modules.totalPages}
                          page={this.modules.pageable.pageNumber!!}
                          onChangePage={this.handleChangePage}/>
                      </>
                    }
                  </>
                }
              </div>
              <SaveSnackBar onClose={() => this.displaySnackbar.set(false)} open={this.displaySnackbar.get()}
                            massage={translation.t('catalogueTab.saved')}/>
            </>
        }
      </>
    );
  }

  private getModuleTitle = (module: QuestionModuleDto): string => {
    if (module.title) {
      return module.title
    } else {
      return translation.t('createCatalogueComponent.noModuleTitle')
    }
  };

  private onSaveClicked() {
    this.selectedModules.map((module, index) => {
      this.newCatalogue.modules.push({position: index + 1, questionModule: module});
    });

    this.validationErrors = isValidQuestionCatalogue(this.newCatalogue);
    if (this.validationErrors === undefined || this.validationErrors.fieldErrors.length === 0) {
      this.saveLoading.set(true);
      createCatalogue(this.newCatalogue).then(
        data => {
          this.saveLoading.set(false);
          if (isValidationErrors(data)) {
            this.validationErrors = data;
          } else {
            this.validationErrors = emptyValidationErrors();
            this.displaySnackbar.set(true);
            this.newCatalogue.title = '';
            this.newCatalogue.modules = [];
            this.selectedModules = [];
          }
        }
      )
    }
  }

  private handleAddModule = (module: QuestionModuleDto) => {
    this.selectedModules.push(module)
  };

  private handleChangePage = (event: any, page: number) => {
    this.pageAble.page = page;
    this.loadModules();
  };

  private loadModules = () => {
    this.loading = true;
    getModules({searchText: this.props.searchText.get()}, this.pageAble).then(page => {
      this.modules = page;
      this.loading = false;
    });
  };

}

export default withStyles(localStyles)(CatalogueTab);
